"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validate = validate;
function validate(value, field) {
    if (isNumericRangeType(field.type))
        return validateNumericRange(value, field);
    if (field.type === 'SINGLE_SELECT')
        return validateSingleSelect(value, field);
    if (field.type === 'WIDTH_HEIGHT')
        return validateWidthHeight(value, field);
    return { valid: true };
}
function isNumericRangeType(type) {
    return (type === 'SLIDER' ||
        type === 'UPSCALE_FACTOR_SLIDER' ||
        type === 'CFG' ||
        type === 'STEPS');
}
function coerceNumber(value) {
    if (typeof value === 'number' && Number.isFinite(value))
        return value;
    if (value && typeof value === 'object' && 'value' in value) {
        const inner = value.value;
        if (typeof inner === 'number' && Number.isFinite(inner))
            return inner;
    }
    if (typeof value === 'string' && !isNaN(Number(value)))
        return Number(value);
    return null;
}
function validateNumericRange(value, field) {
    const display = (field.display || {});
    const numValue = coerceNumber(value);
    if (numValue === null) {
        return { valid: false, reason: 'Invalid numeric value' };
    }
    // Discrete-options branch (UPSCALE_FACTOR_SLIDER with explicit options).
    if (Array.isArray(display.options) && display.options.length > 0) {
        const matched = display.options.some((opt) => Number(opt.value) === numValue || opt.value === numValue);
        if (!matched) {
            return { valid: false, reason: 'Value is not one of the allowed options' };
        }
        return { valid: true };
    }
    if (numValue < display.min) {
        return { valid: false, reason: `Value below minimum (${display.min})` };
    }
    if (numValue > display.max) {
        return { valid: false, reason: `Value above maximum (${display.max})` };
    }
    // Step alignment: enforced for SLIDER / UPSCALE_FACTOR_SLIDER / CFG / STEPS
    // alike, defaulting step to 1 when missing (matching web's validator).
    const step = typeof display.step === 'number' ? display.step : 1;
    const stepsFromMin = (numValue - display.min) / step;
    const rounded = Math.round(stepsFromMin);
    const expected = display.min + rounded * step;
    const tolerance = step * 0.0001;
    if (Math.abs(numValue - expected) > tolerance) {
        return { valid: false, reason: `Value doesn't align with step (${step})` };
    }
    return { valid: true };
}
function getSingleSelectOptions(display) {
    if (!display)
        return [];
    if (display.text && Array.isArray(display.text.options))
        return display.text.options;
    if (display.radio && Array.isArray(display.radio.options))
        return display.radio.options;
    if (display.image && Array.isArray(display.image.options))
        return display.image.options;
    return [];
}
function validateSingleSelect(value, field) {
    const options = getSingleSelectOptions(field.display);
    if (options.length === 0) {
        return { valid: false, reason: 'No valid options available' };
    }
    let compare = value;
    if (value && typeof value === 'object' && 'value' in value) {
        compare = value.value;
    }
    // Exact match first, so an option declared with a `null` value (a "None"
    // choice) validates as the real option it is. See `matchesByValue` in
    // heal.ts for why the looser String() pass below is string/number only.
    if (options.some((opt) => opt.value === compare)) {
        return { valid: true };
    }
    if (typeof compare !== 'string' && typeof compare !== 'number') {
        return { valid: false, reason: 'Invalid option format' };
    }
    const match = options.some((opt) => (typeof opt.value === 'string' || typeof opt.value === 'number') &&
        String(opt.value) === String(compare));
    return match ? { valid: true } : { valid: false, reason: 'Invalid option' };
}
function validateWidthHeight(value, field) {
    const display = field.display;
    const select = display && display.select;
    if (!select)
        return { valid: true };
    const options = Array.isArray(select.options) ? select.options : [];
    if (options.length === 0) {
        return { valid: false, reason: 'No valid resolution options available' };
    }
    if (select.format === 'wh') {
        if (value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            typeof value.width === 'number' &&
            typeof value.height === 'number') {
            const w = value.width;
            const h = value.height;
            const match = options.some((opt) => opt.width === w && opt.height === h);
            return match
                ? { valid: true }
                : { valid: false, reason: 'Invalid width/height combination' };
        }
        return { valid: false, reason: 'Invalid width/height format' };
    }
    // Default to 'value' format.
    let compare = value;
    if (value && typeof value === 'object' && 'value' in value) {
        compare = value.value;
    }
    if (typeof compare !== 'string' && typeof compare !== 'number') {
        return { valid: false, reason: 'Invalid resolution option format' };
    }
    const match = options.some((opt) => opt.value === compare || String(opt.value) === String(compare));
    return match ? { valid: true } : { valid: false, reason: 'Invalid resolution option' };
}
